import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  AfterViewInit,
  ElementRef,
  ViewChild,
  OnDestroy,
  TemplateRef,
  ComponentRef,
  ViewContainerRef,
  Injector,
  createComponent,
  EnvironmentInjector,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as L from 'leaflet';
import { MapPopoverComponent } from '../map-popover/map-popover.component';

export interface MapMarker {
  position: L.LatLngExpression;
  color: 'red' | 'orange' | 'blue' | 'events-high' | 'events-medium' | 'events-low' | 'alarms-high' | 'alarms-medium' | 'alarms-low' | 'network-high' | 'network-medium' | 'network-low';
  number?: string | number;
  popupData?: any; // Datos para el componente del popover
  popupTitle?: string;
  distributorId?: string; // For navigation purposes
}

@Component({
  selector: 'ui-map',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div #mapContainer class="w-full h-full min-h-[400px]"></div>
    <div #popoverContainer style="display: none;"></div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class MapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;
  @ViewChild('popoverContainer', { static: true, read: ViewContainerRef })
  popoverContainer!: ViewContainerRef;

  private map!: L.Map;
  private markersLayer = L.layerGroup();
  private markersMap = new Map<string, L.Marker>(); // Store markers by position key
  private activePopoverComponent: ComponentRef<MapPopoverComponent> | null = null; // Store active popover component
  private popoverComponents = new Map<string, ComponentRef<MapPopoverComponent>>(); // Store popover components by marker position
  private userHasInteractedWithZoom = false; // Track if user has manually zoomed

  markers = input<MapMarker[]>([]);
  center = input<L.LatLngExpression>([40.4168, -3.7038]);
  zoom = input<number>(6);
  height = input<string>('100%');
  markerToReopen = input<L.LatLng | null>(null);
  loading = input<boolean>(false);

  // Output event when a marker is clicked
  markerClick = output<MapMarker>();

  // Output event when notification icon is clicked in popover
  notificationIconClick = output<any>();

  // Output event when bell icon is clicked in popover
  bellIconClick = output<any>();

  // Output event when line chart icon is clicked in popover
  lineChartIconClick = output<any>();

  // Output event when line is clicked in popover
  lineClick = output<{ distributorId: string; centerId: string; rsabtId: string; lineId: string; vtnId?: string; url?: string }>();

  constructor(
    private injector: Injector,
    private environmentInjector: EnvironmentInjector,
  ) {
    let previousMarkerPositions: string = '';

    // Effect para detectar cambios en los marcadores
    effect(() => {
      const markersData = this.markers(); // Esto hace que el effect sea reactivo a los cambios
      if (this.map && markersData) {
        this.addMarkers();

        // Only center map if marker positions have actually changed
        const currentMarkerPositions = markersData
          .map(m => `${(m.position as [number, number])[0]},${(m.position as [number, number])[1]}`)
          .sort()
          .join('|');

        if (previousMarkerPositions !== currentMarkerPositions) {
          this.centerMap();
          previousMarkerPositions = currentMarkerPositions;
        }

        // Check if we need to reopen a popup
        const reopenMarker = this.markerToReopen();
        if (reopenMarker) {
          setTimeout(() => this.reopenPopup(reopenMarker), 100);
        }
      }
    });
  }

  ngAfterViewInit() {
    this.initMap();
    this.addMarkers();
    this.centerMap();
  }

  ngOnDestroy() {
    // Clean up all popover components
    this.popoverComponents.forEach(component => {
      component.destroy();
    });
    this.popoverComponents.clear();

    if (this.activePopoverComponent) {
      this.activePopoverComponent.destroy();
      this.activePopoverComponent = null;
    }
    if (this.map) {
      this.map.remove();
    }
  }

  private initMap() {
    const baseMapUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    this.map = L.map(this.mapContainer.nativeElement, {
      center: this.center(),
      zoom: this.zoom(),
      minZoom: 2,
      maxZoom: 18,
      worldCopyJump: false, // Evita salto entre copias del mundo
      maxBounds: [
        [-90, -180],
        [90, 180],
      ], // Limita el área visible al mundo
    });

    L.tileLayer(baseMapUrl, {
      attribution: '© OpenStreetMap contributors',
      noWrap: true, // Evita que los tiles se repitan como mosaico
      bounds: [
        [-90, -180],
        [90, 180],
      ], // Limita los tiles al área del mundo
    }).addTo(this.map);

    // Agregar la capa de marcadores al mapa
    this.markersLayer.addTo(this.map);

    // Track user zoom interactions to prevent auto-centering after manual zoom
    this.map.on('zoomend', () => {
      this.userHasInteractedWithZoom = true;
    });
  }

  private createCustomIcon(
    color: 'red' | 'orange' | 'blue' | 'events-high' | 'events-medium' | 'events-low' | 'alarms-high' | 'alarms-medium' | 'alarms-low' | 'network-high' | 'network-medium' | 'network-low',
    number?: string | number,
  ): L.DivIcon {
    const size = 28;

    // For events, alarms and network colors, use CSS variables; for basic colors, use fixed values
    const isEventColor = color.startsWith('events-');
    const isAlarmColor = color.startsWith('alarms-');
    const isNetworkColor = color.startsWith('network-');

    let backgroundColor: string;
    let cssClass: string;

    if (isEventColor || isAlarmColor) {
      // For events and alarms, use CSS variables and specific classes
      cssClass = `custom-marker marker-${color}`;
      // Set a fallback color, but CSS will override with the variable
      backgroundColor = 'transparent';
    } else if (isNetworkColor) {
      // For network colors, use specific hex values with white text
      const networkColorMap = {
        'network-low': '#0097FB',    // Low - blue
        'network-medium': '#FF8A00', // Medium - orange
        'network-high': '#FF4D4F',   // High - red
      };
      backgroundColor = networkColorMap[color as 'network-low' | 'network-medium' | 'network-high'];
      cssClass = `custom-marker marker-${color}`;
    } else {
      // For basic colors, keep the existing behavior
      const colorMap = {
        red: '#FF4D4F',
        orange: '#FF8A00',
        blue: '#0097FB',
      };
      backgroundColor = colorMap[color as 'red' | 'orange' | 'blue'];
      cssClass = `custom-marker marker-${color}`;
    }

    const html = `
      <div class="${cssClass}" style="width: ${size}px; height: ${size}px; ${!isEventColor && !isAlarmColor ? `background-color: ${backgroundColor};` : ''} ${isNetworkColor ? 'color: white;' : ''}">
        ${number !== undefined ? number : ''}
      </div>
    `;

    return L.divIcon({
      html: html,
      className: 'leaflet-div-icon',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
    });
  }

  private createPopoverContent(markerInfo: MapMarker): HTMLElement | null {
    if (!markerInfo.popupData && !markerInfo.popupTitle) {
      return null;
    }

    const positionKey = this.getPositionKey(markerInfo.position);

    // Clean up existing component for this position
    const existingComponent = this.popoverComponents.get(positionKey);
    if (existingComponent) {
      existingComponent.destroy();
    }

    // Crear el componente MapPopover dinámicamente
    const componentRef: ComponentRef<MapPopoverComponent> = createComponent(
      MapPopoverComponent,
      {
        environmentInjector: this.environmentInjector,
        elementInjector: this.injector,
      },
    );

    // Store the component for cleanup later
    this.popoverComponents.set(positionKey, componentRef);

    // Configurar los inputs del componente
    if (markerInfo.popupData) {
      // Detect the type of data and set the appropriate input
      if (markerInfo.popupData.totalEvents !== undefined && markerInfo.popupData.totalAlerts !== undefined) {
        // Both events and alerts data (unified response)
        componentRef.setInput('bothData', markerInfo.popupData);
      } else if (markerInfo.popupData.totalEvents !== undefined) {
        // Events data only
        componentRef.setInput('eventsData', markerInfo.popupData);
      } else if (markerInfo.popupData.totalAlerts !== undefined) {
        // Alarms data only
        componentRef.setInput('alarmsData', markerInfo.popupData);
      } else {
        // Fallback to legacy input
        componentRef.setInput('data', markerInfo.popupData);
      }

      // Set distributorId if available
      if (markerInfo.distributorId) {
        componentRef.setInput('distributorId', markerInfo.distributorId);
      }

      // Set loading state
      componentRef.setInput('loading', this.loading());

      // Subscribe to component outputs for event handling
      componentRef.instance.notificationIconClick.subscribe((data: any) => {
        this.notificationIconClick.emit(data);
      });

      // Subscribe to bell icon click for alarms
      componentRef.instance.bellIconClick.subscribe((data: any) => {
        this.bellIconClick.emit(data);
      });

      // Subscribe to line chart icon click
      componentRef.instance.lineChartIconClick.subscribe((data: any) => {
        this.lineChartIconClick.emit(data);
      });

      // Subscribe to line click for navigation
      componentRef.instance.lineClick.subscribe((navigationData: any) => {
        this.lineClick.emit(navigationData);
      });
    }

    // Detectar cambios para que se renderice el contenido
    componentRef.changeDetectorRef.detectChanges();

    // Return the actual DOM element instead of HTML string
    return componentRef.location.nativeElement;
  }

  private addMarkers() {
    const markerData = this.markers();

    // Limpiar marcadores existentes
    this.markersLayer.clearLayers();
    this.markersMap.clear();

    // Clean up existing popover components
    this.popoverComponents.forEach(component => {
      component.destroy();
    });
    this.popoverComponents.clear();

    if (markerData.length > 0) {
      markerData.forEach((markerInfo) => {
        const customIcon = this.createCustomIcon(
          markerInfo.color,
          markerInfo.number,
        );
        const marker = L.marker(markerInfo.position, { icon: customIcon });

        const popoverElement = this.createPopoverContent(markerInfo);
        if (popoverElement) {
          marker.bindPopup(popoverElement, {
            autoPan: false, // Prevent automatic panning when popup opens
            keepInView: false, // Don't adjust view to keep popup in view
          });
        }

        // Add click event listener to emit markerClick
        marker.on('click', () => {
          this.markerClick.emit(markerInfo);
        });

        this.markersLayer.addLayer(marker);

        // Store marker reference by position key
        const posKey = this.getPositionKey(markerInfo.position);
        this.markersMap.set(posKey, marker);
      });
    }
  }

  private getPositionKey(position: L.LatLngExpression): string {
    const latLng = L.latLng(position as [number, number]);
    return `${latLng.lat},${latLng.lng}`;
  }

  private reopenPopup(latLng: L.LatLng) {
    const posKey = this.getPositionKey([latLng.lat, latLng.lng]);
    const marker = this.markersMap.get(posKey);
    if (marker && this.map) {
      const target = L.latLng(latLng.lat, latLng.lng);
      // Abrir el popup después de centrar/panear para que se muestre correctamente
      this.map.once('moveend', () => {
        marker.openPopup();
      });
      // Centrar en el punto sin cambiar el zoom actual (panTo mantiene el zoom)
      this.map.flyTo(target, this.map.getZoom(), { animate: true, duration: 0.1 });
    }
  }

  private centerMap() {
    const markerData = this.markers();

    // If user has manually zoomed, don't auto-adjust the view at all
    if (this.userHasInteractedWithZoom) {
      return;
    }

    if (markerData.length > 1) {
      // Si hay múltiples marcadores, ajustar a todos
      const positions = markerData.map((marker) => marker.position);
      const bounds = L.latLngBounds(positions);
      this.map.fitBounds(bounds);
    } else if (markerData.length === 1) {
      // Si hay un solo marcador, centrarlo SIN cambiar el zoom
      // Usar panTo en lugar de setView para mantener el zoom actual
      this.map.panTo(markerData[0].position);
    } else {
      // Si no hay marcadores, usar el centro por defecto
      this.map.setView(this.center(), this.zoom());
    }
  }

  // Método público para agregar marcadores dinámicamente
  addMarker(markerInfo: MapMarker): L.Marker | null {
    if (this.map) {
      const customIcon = this.createCustomIcon(
        markerInfo.color,
        markerInfo.number,
      );
      const marker = L.marker(markerInfo.position, { icon: customIcon });

      const popoverContent = this.createPopoverContent(markerInfo);
      if (popoverContent) {
        marker.bindPopup(popoverContent, {
          autoPan: false, // Prevent automatic panning when popup opens
          keepInView: false, // Don't adjust view to keep popup in view
        });
      }

      // Add click event listener to emit markerClick
      marker.on('click', () => {
        this.markerClick.emit(markerInfo);
      });

      this.markersLayer.addLayer(marker);
      return marker;
    }
    return null;
  }

  // Método público para limpiar todos los marcadores
  clearMarkers() {
    this.markersLayer.clearLayers();
  }

  // Método público para obtener la instancia del mapa
  getMap(): L.Map | null {
    return this.map || null;
  }
}
